import React from 'react';
import { useBilling } from '../../context/BillingContext';
import { Settings, Bell } from 'lucide-react';
import SyncBadge from '../SyncBadge';
import logo from '../../assets/logo.png';

interface User {
    email: string;
    role: string;
}

interface UserMetadata {
    currentProgram?: string;
    accessStatus?: string;
    [key: string]: any;
}

interface AppHeaderProps {
    user: User | null;
    userMetadata: UserMetadata | null;
    hasNotifications: boolean;
    onNotifClick: () => void;
    onSettingsClick: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
    user,
    userMetadata,
    hasNotifications,
    onNotifClick,
    onSettingsClick
}) => {
    const { getStatusDisplay } = useBilling();
    const billing = getStatusDisplay();

    const colorClasses: Record<string, string> = {
        green: 'text-emerald-600 bg-emerald-50 border-emerald-100',
        blue: 'text-blue-600 bg-blue-50 border-blue-100',
        red: 'text-red-600 bg-red-50 border-red-100',
        orange: 'text-orange-600 bg-orange-50 border-orange-100',
        gray: 'text-slate-400 bg-slate-50 border-slate-100'
    };

    const statusStyle = colorClasses[billing.color] || colorClasses.gray;

    return (
        <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-6 py-4 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-3">
                <div className="bg-blue-50 p-2 rounded-xl">
                    <img src={logo} alt="Logo" className="h-8 w-8 object-contain" />
                </div>
                <div>
                    <h1 className="text-xl font-black text-slate-800 tracking-tight leading-none uppercase">BizBuilder Platform</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-blue-700 font-black uppercase tracking-widest opacity-80">
                            {userMetadata?.currentProgram || (user?.role === 'CLIENT' ? 'Base Access' : `${user?.role} HUB`)}
                        </span>
                        <span className="text-slate-300">•</span>
                        <div className={`px-1.5 py-0.5 rounded-md border text-[8px] font-black uppercase tracking-tighter ${statusStyle}`}>
                            {billing.label}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <div className="relative">
                    <button
                        onClick={onNotifClick}
                        className="p-2.5 rounded-xl hover:bg-slate-100 text-slate-500 transition-all active:scale-90 relative"
                    >
                        <Bell size={20} />
                        {hasNotifications && (
                            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                        )}
                    </button>
                </div>
                <SyncBadge />
                <button
                    onClick={onSettingsClick}
                    className="p-2.5 rounded-xl hover:bg-slate-100 text-slate-500 transition-all active:scale-90"
                >
                    <Settings size={20} />
                </button>
            </div>
        </header>
    );
};

export default AppHeader;
